import React from 'react';
import { useMemo } from 'react';
import isEmpty from 'lodash.isempty';
import styled from 'styled-components';
import { Page, Loading, Typo } from '../../../ui';
import { usePage, usePageStatus, useCourseSectionById, useCoursePageById } from '../hooks';
import { STATUS_FAILED, coursePageNavigation } from '../../../utils/constants';
import { useCourseTheme } from '../../course/hooks';
import Module from '../../Module';
import Row from '../../Row';
import Quiz from './Quiz';
import { CONTENT_STANDARD } from '../../../utils/media';
import { getUserThemeColor } from '../../../ui/theme/colors';
import { Breadcrumbs } from '@mui/material';
import NoAccessPage from '../../../ui/Page/NoAccessPage';
import PageSection from '../../../ui/Page/PageSection';

const CrumbsWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 20px;
`;

const CoursePage = ({ pageId, isQuiz, quizData, isNotesOpen, pageInfo }) => {
  // Use pageInfo prop (from RTKQ) as primary source, fallback to local state
  const localPage = usePage();
  const pageStatus = usePageStatus();
  const courseTheme = useCourseTheme();

  // Prefer pageInfo prop over local state for better cache handling
  const page = pageInfo || localPage;

  const pageFromViewCourseState = useCoursePageById(pageId);
  const activeSection = useCourseSectionById(pageFromViewCourseState?.sectionId);
  const themeColor = useMemo(() => getUserThemeColor(courseTheme?.color), [courseTheme]);

  const renderPageContent = () => {
    const pageRows = page?.pageRows || [];

    return (
      <>
        {pageRows.map((pageRow, index) => (
          <React.Fragment key={`pageRow-${pageRow.id}`}>
            <Row
              pageRow={pageRow}
              isNotesOpen={isNotesOpen}
              renderModule={(module, props) => <Module key={props.key} module={module} columns={props.$columns} themeColor={themeColor} />}
            />
          </React.Fragment>
        ))}
      </>
    );
  };

  const renderPageDiploma = () => {
    return <Module key='diploma' module={{ data: {}, type: { name: 'diploma' } }} columns={1} themeColor={themeColor} />;
  };

  const breadcrumbs = useMemo(() => {
    return [
      <Typo.MediumText key={`section-${activeSection?.name}`}>{activeSection?.name}</Typo.MediumText>,
      <Typo.MediumText key={`page-${page?.name}`}>{page?.name}</Typo.MediumText>,
      // <Typo.MediumText> currentPage / totalPages</Typo.MediumText>,
    ];
  }, [activeSection, page]);

  return (
    <Page substractBottom={coursePageNavigation}>
      <CrumbsWrapper>
        <Breadcrumbs separator='›' aria-label='breadcrumb'>
          {breadcrumbs}
        </Breadcrumbs>
      </CrumbsWrapper>
      <PageSection align='flex-start' gap='60' size={CONTENT_STANDARD}>
        {pageId === 'diploma' ? (
          renderPageDiploma()
        ) : // If we have page data (from pageInfo prop or local state), show content
        !isEmpty(page) ? (
          isQuiz ? (
            <Quiz quizData={quizData} pageInfo={page} themeColor={themeColor} pageRows={page?.pageRows} />
          ) : (
            renderPageContent()
          )
        ) : // Show error if local pageStatus failed, otherwise loading
        pageStatus === STATUS_FAILED ? (
          <NoAccessPage />
        ) : (
          <Loading themeColor={themeColor} />
        )}
      </PageSection>
    </Page>
  );
};

export default CoursePage;
